import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import Seo from "@/components/Seo";
import Container from "@/components/marketing/Container";
import SectionHeading from "@/components/marketing/SectionHeading";
import OutletDetailHero from "@/components/marketing/service/OutletDetailHero";
import OutletOptions from "@/components/marketing/service/OutletOptions";
import ServiceFAQ from "@/components/marketing/service/ServiceFAQ";
import CTASection from "@/components/marketing/CTASection";
import { Button } from "@/components/ui/button";
import { FileText, Link2, Search, Clock, ShieldCheck, Globe, TrendingUp, ArrowRight } from "lucide-react";
import { formatPrice } from "@/lib/utils";

// Fallback for the Product-schema image when an outlet has no logo URL
// (e.g. text-fallback outlets). 1200×630 PNG — a valid Google image format.
const OG_IMAGE = "https://media.base44.com/images/public/6a460432963500ce22897197/b7b770edf_generated_image.png";

const WHATS_INCLUDED = [
  { icon: FileText, title: "Guaranteed Publication", desc: "Your article is published on the outlet you chose, or you get a full refund. No exceptions." },
  { icon: Link2, title: "Backlink to Your Site", desc: "A backlink from a high-authority domain. Dofollow or nofollow, disclosed upfront per outlet." },
  { icon: Search, title: "Indexed in Search", desc: "Your placement is indexed by Google and other search engines, delivering lasting SEO value." },
  { icon: Clock, title: "Fast Turnaround", desc: "Same-day submission. Typical publication within 3–7 business days depending on the outlet." },
  { icon: ShieldCheck, title: "Fully Attributed", desc: "Your article is published with full attribution and your brand name, not anonymously." },
  { icon: Globe, title: "Audience Reach", desc: "Tap into the outlet's existing readership across your target region and sector." },
];

export default function OutletDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dbOutlet, setDbOutlet] = useState(null);
  const [dbFetched, setDbFetched] = useState(false);
  const [price, setPrice] = useState(null);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  const outlet = dbOutlet;

  useEffect(() => {
    let active = true;
    base44.functions
      .invoke("getOutletBySlug", { slug })
      .then((res) => {
        if (!active) return;
        if (res?.data?.redirect_slug) {
          navigate(`/outlets/${res.data.redirect_slug}`, { replace: true });
          return;
        }
        setDbOutlet(res?.data?.outlet || null);
        setOptions(res?.data?.options || null);
      })
      .catch(() => {})
      .finally(() => { if (active) setDbFetched(true); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!dbOutlet) { if (dbFetched) setLoading(false); return; }
    if (options && options.length > 1) {
      const prices = options.map((o) => o.price).filter((p) => p != null);
      setPrice(prices.length > 0 ? Math.min(...prices) : null);
    } else {
      setPrice(dbOutlet.price ?? null);
    }
    setLoading(false);
  }, [dbOutlet, dbFetched, options]);

  if (!dbOutlet && !dbFetched) {
    return (
      <div className="bg-canvas min-h-[60vh] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-cobalt/20 border-t-cobalt rounded-full animate-spin" />
      </div>
    );
  }

  if (!dbOutlet) {
    return (
      <div className="bg-canvas min-h-[60vh] flex items-center justify-center">
        <Container className="text-center">
          <h1 className="font-heading text-3xl font-bold text-bone mb-4">Outlet not found</h1>
          <p className="text-steel mb-8">This outlet page doesn't exist or may have been moved.</p>
          <Link to="/services/guaranteed-placements">
            <Button className="bg-cobalt hover:bg-cobalt/90 text-white">
              Browse All Outlets <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </Container>
      </div>
    );
  }

  const isMultiOption = !!(options && options.length > 1);
  const priceDisplay = price != null ? `$${formatPrice(price)}` : null;
  const seoTitle = priceDisplay
    ? `Guaranteed Placement & Guest Post on ${outlet.name} ${isMultiOption ? "from" : "for"} ${priceDisplay} | Digital24`
    : `Guaranteed Placement & Guest Post on ${outlet.name} | Digital24`;
  const seoDesc = `Get a guaranteed placement or guest post on ${outlet.name} (DA ${outlet.da}, ${outlet.sector}). Published, indexed, with a backlink to your site. Create your free account and order today.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `Guaranteed Placement on ${outlet.name}`,
    "description": seoDesc,
    "image": outlet.logo || OG_IMAGE,
    "brand": { "@type": "Brand", "name": "Digital24" },
    ...(price != null && {
      offers: {
        "@type": "Offer",
        price: String(price),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    }),
  };

  const faqs = [
    { q: `Can I publish a guest post on ${outlet.name}?`, a: `Yes. ${outlet.name} accepts guest posts, contributor articles, and press releases through our guaranteed placement service. You choose the content type and we guarantee publication.` },
    { q: `How much does a guaranteed placement on ${outlet.name} cost?`, a: priceDisplay ? `A guaranteed placement on ${outlet.name} starts at ${priceDisplay}. The price includes guaranteed publication, a backlink to your site, and search engine indexing.` : `Pricing for ${outlet.name} varies by content type and requirements. Create a free account to see live pricing, or contact our team for a quote.` },
    { q: `Is the backlink from ${outlet.name} dofollow?`, a: `Link type (dofollow or nofollow) varies by publication. We disclose the exact link type for each outlet before you order, so you know what you're getting.` },
    { q: `How long until my article is published on ${outlet.name}?`, a: `Submission is same-day. Publication timing varies by outlet, typically within 3–7 business days. We share the expected turnaround before you order.` },
    { q: `What happens if my article doesn't get published?`, a: `If we cannot get your article published on ${outlet.name}, you get a full refund. That's our guarantee. Published or your money back.` },
    { q: `Is the placement on ${outlet.name} guaranteed?`, a: `All placements are subject to editorial review. The publication may label content as advertorial or decline the story, in which case a full refund is issued.` },
  ];

  const STEPS = [
    { num: "01", title: "Create Your Account", desc: "Sign up free. No credit card required. Takes less than a minute." },
    { num: "02", title: `Select ${outlet.name}`, desc: `Choose this outlet from our guaranteed placements catalogue and submit your content.` },
    { num: "03", title: "Get Published", desc: "We handle the placement. You receive the live published link with a full report." },
  ];

  return (
    <div className="bg-canvas">
      <Seo title={seoTitle} description={seoDesc} jsonLd={jsonLd} url={typeof window !== "undefined" ? window.location.href : undefined} />

      <OutletDetailHero outlet={outlet} price={price} loading={loading} priceWord={isMultiOption ? "from" : "for"} />

      {isMultiOption && <OutletOptions outlet={outlet} options={options} />}

      {/* What You Get */}
      <section className="py-7 sm:py-9 lg:py-12">
        <Container>
          <SectionHeading
            tag="What's Included"
            title={`What You Get with Your ${outlet.name} Placement`}
            subtitle="Every guaranteed placement includes publication, a backlink, and full transparency on what you're buying."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {WHATS_INCLUDED.map((item) => (
              <div key={item.title} className="bg-white border border-border rounded-xl p-6 hover:border-cobalt/30 transition-colors">
                <item.icon className="w-8 h-8 text-cobalt mb-4" strokeWidth={1.75} />
                <h3 className="font-heading text-lg font-bold text-bone mb-2">{item.title}</h3>
                <p className="text-sm text-steel leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Why This Outlet Matters */}
      <section className="py-7 sm:py-9 lg:py-12 bg-slate/50">
        <Container>
          <SectionHeading
            tag="SEO Value"
            title={`Why a Placement on ${outlet.name} Matters`}
            subtitle={`A backlink from ${outlet.name} carries significant SEO authority and puts your brand in front of a relevant ${outlet.sector.toLowerCase()} audience.`}
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center bg-white border border-border rounded-xl p-6">
              <TrendingUp className="w-8 h-8 text-cobalt mx-auto mb-3" strokeWidth={1.75} />
              <div className="font-heading text-2xl font-bold text-bone">DA {outlet.da}</div>
              <div className="text-xs text-steel uppercase tracking-wider mt-1">Domain Authority</div>
            </div>
            <div className="text-center bg-white border border-border rounded-xl p-6">
              <Globe className="w-8 h-8 text-cobalt mx-auto mb-3" strokeWidth={1.75} />
              <div className="font-heading text-2xl font-bold text-bone">{outlet.region}</div>
              <div className="text-xs text-steel uppercase tracking-wider mt-1">Region</div>
            </div>
            <div className="text-center bg-white border border-border rounded-xl p-6">
              <FileText className="w-8 h-8 text-cobalt mx-auto mb-3" strokeWidth={1.75} />
              <div className="font-heading text-2xl font-bold text-bone">{outlet.sector}</div>
              <div className="text-xs text-steel uppercase tracking-wider mt-1">Sector</div>
            </div>
            {outlet.tier && (
              <div className="text-center bg-white border border-border rounded-xl p-6">
                <ShieldCheck className="w-8 h-8 text-cobalt mx-auto mb-3" strokeWidth={1.75} />
                <div className="font-heading text-2xl font-bold text-bone">{outlet.tier}</div>
                <div className="text-xs text-steel uppercase tracking-wider mt-1">Outlet Tier</div>
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-7 sm:py-9 lg:py-12">
        <Container>
          <SectionHeading tag="How It Works" title={`Get Published on ${outlet.name} in 3 Steps`} />
          <div className="grid sm:grid-cols-3 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {STEPS.map((step) => (
              <div key={step.num} className="text-center">
                <span className="font-heading text-4xl font-bold text-cobalt/20 block mb-3">{step.num}</span>
                <h3 className="font-heading text-lg font-bold text-bone mb-2">{step.title}</h3>
                <p className="text-sm text-steel leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/register">
              <Button size="lg" className="bg-cobalt hover:bg-cobalt/90 text-white px-8">
                Create Free Account <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      <ServiceFAQ faqs={faqs} tag="FAQ" title={`${outlet.name} Placement FAQ`} />

      <CTASection
        eyebrow="Get Started"
        headline={`Ready to Publish on ${outlet.name}?`}
        subtitle="Create your free account and place your order in minutes. No credit card required to sign up."
      />
    </div>
  );
}